import {
  HomePageStarredEntities,
  HomePageToolkit,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { Content, Page } from '@backstage/core-components';
import { enlivioColors } from '../../theme';
import {
  Grid,
  makeStyles,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
} from '@material-ui/core';
import {
  RiAmazonFill,
  RiArrowRightLine,
  RiAtomLine,
  RiBookOpenLine,
  RiBox3Line,
  RiCloudLine,
  RiGitBranchLine,
  RiGithubFill,
  RiWindowsFill,
} from '@remixicon/react';

const useStyles = makeStyles(theme => ({
  heroSection: {
    textAlign: 'center',
    padding: theme.spacing(4, 0, 2),
  },
  heroTitle: {
    fontWeight: 400,
    fontSize: '2.5rem',
    color:
      theme.palette.type === 'dark'
        ? enlivioColors.offWhite
        : enlivioColors.midnightGreen,
    marginBottom: theme.spacing(1),
  },
  heroSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    maxWidth: 600,
    margin: '0 auto',
  },
  searchBarInput: {
    maxWidth: '60vw',
    margin: 'auto',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    boxShadow: 'none',
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  engineeringCard: {
    background:
      'linear-gradient(270deg, #001817 1px, transparent 0) 0 0 / 24px 24px, linear-gradient(0deg, #001817 1px, transparent 0) 0 0 / 24px 24px, linear-gradient(to left bottom, #001817, #1a2f2e)',
    color: enlivioColors.offWhite,
    borderRadius: 0,
    height: '100%',
  },
  genesisCard: {
    backgroundColor: '#000',
    color: enlivioColors.offWhite,
    borderRadius: 0,
    height: '100%',
  },
  cardContent: {
    padding: theme.spacing(4),
  },
  cardIcon: {
    display: 'inline-flex',
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
  },
  cardSubtitle: {
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  cardButton: {
    backgroundColor: enlivioColors.vibrantGreen,
    color: enlivioColors.midnightGreen,
    border: `1px solid ${enlivioColors.vibrantGreen}`,
    fontWeight: 500,
    padding: theme.spacing(1, 3),
    borderRadius: 0,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: enlivioColors.midnightGreen,
      color: enlivioColors.vibrantGreen,
    },
  },
  genesisButton: {
    backgroundColor: enlivioColors.offWhite,
    color: enlivioColors.midnightGreen,
    border: `1px solid ${enlivioColors.offWhite}`,
    fontWeight: 500,
    padding: theme.spacing(1, 3),
    borderRadius: 0,
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: enlivioColors.vibrantGreen,
      borderColor: enlivioColors.vibrantGreen,
      color: enlivioColors.midnightGreen,
    },
  },
  betaChip: {
    backgroundColor: 'transparent',
    color: enlivioColors.vibrantGreen,
    border: `1px solid ${enlivioColors.vibrantGreen}`,
    borderRadius: 0,
    fontSize: '0.75rem',
    fontWeight: 400,
    letterSpacing: 0,
    lineHeight: 1.5,
    height: 'auto',
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 2),
    whiteSpace: 'nowrap' as const,
    '& .MuiChip-label': {
      padding: 0,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  contributeBanner: {
    backgroundColor: enlivioColors.midnightGreen,
    color: enlivioColors.offWhite,
    padding: theme.spacing(1.5, 2),
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
  },
  bannerText: {
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  bannerLink: {
    color: enlivioColors.midnightGreen,
    fontWeight: 500,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 0,
    backgroundColor: enlivioColors.vibrantGreen,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const TOOLKIT_TOOLS = [
  {
    url: 'https://github.com/emisgroup',
    label: 'GitHub',
    icon: <RiGithubFill />,
  },
  {
    url: 'https://emisgroup.visualstudio.com/',
    label: 'Azure DevOps',
    icon: <RiGitBranchLine />,
  },
  {
    url: 'https://centraluk.jfrog.io/',
    label: 'JFrog Artifactory',
    icon: <RiBox3Line />,
  },
  {
    url: 'https://emisgroup.awsapps.com/start#/',
    label: 'AWS',
    icon: <RiAmazonFill />,
  },
  {
    url: 'https://portal.azure.com/',
    label: 'Azure Portal',
    icon: <RiWindowsFill />,
  },
  { url: 'https://app.netlify.com/', label: 'Netlify', icon: <RiCloudLine /> },
];

const EngineeringDocsCard = () => {
  const classes = useStyles();

  return (
    <Card className={classes.engineeringCard} elevation={0}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardIcon}>
          <RiBookOpenLine size={28} color={enlivioColors.vibrantGreen} />
        </Box>
        <Typography variant="h5" className={classes.cardTitle}>
          Engineering Docs
        </Typography>
        <Typography variant="body1" className={classes.cardSubtitle}>
          Explore our engineering documentation, architecture guides, best
          practices, and technical standards.
        </Typography>
        <Button
          className={classes.cardButton}
          href="https://engineering.emis-x.uk"
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<RiArrowRightLine size={18} />}
        >
          Visit Documentation
        </Button>
      </CardContent>
    </Card>
  );
};

const GenesisCard = () => {
  const classes = useStyles();

  return (
    <Card className={classes.genesisCard} elevation={0}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardIcon}>
          <RiAtomLine size={28} color={enlivioColors.vibrantGreen} />
        </Box>
        <Box className={classes.titleContainer}>
          <Typography variant="h5" className={classes.cardTitle}>
            Genesis
          </Typography>
          <Chip label="Beta" className={classes.betaChip} size="small" />
        </Box>
        <Typography variant="body1" className={classes.cardSubtitle}>
          Learn about our upcoming platform for hosting containerised workloads
          with built in networking, API management, security & monitoring.
        </Typography>
        <Button
          className={classes.genesisButton}
          href="https://engineering.emis-x.uk/genesis"
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<RiArrowRightLine size={18} />}
        >
          Learn More
        </Button>
      </CardContent>
    </Card>
  );
};

export const HomePage = () => {
  const classes = useStyles();

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <Grid container justifyContent="center" spacing={6}>
            <Grid item xs={12}>
              <Box className={classes.contributeBanner}>
                <Typography className={classes.bannerText}>
                  Help us improve the Developer Hub
                </Typography>
                <a
                  href="https://github.com/emisgroup/emisx-dev-portal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.bannerLink}
                >
                  <RiGithubFill size={18} />
                  Contribute on GitHub
                </a>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.heroSection}>
                <Typography variant="h1" className={classes.heroTitle}>
                  Developer Hub
                </Typography>
                <Typography variant="body1" className={classes.heroSubtitle}>
                  Discover software, services, APIs, documentation & developer
                  tools
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12} justifyContent="center">
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: classes.searchBarInput,
                    notchedOutline: classes.searchBarOutline,
                  },
                }}
                placeholder="Search"
              />
            </Grid>
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12} md={6}>
                <EngineeringDocsCard />
              </Grid>
              <Grid item xs={12} md={6}>
                <GenesisCard />
              </Grid>
            </Grid>
            <Grid container item xs={12} spacing={3}>
              <Grid item xs={12} md={6}>
                <HomePageStarredEntities title="Favourites" />
              </Grid>
              <Grid item xs={12} md={6}>
                <HomePageToolkit tools={TOOLKIT_TOOLS} />
              </Grid>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
